import {Component, afterNextRender, ElementRef, inject, OnDestroy, OnInit, signal, ChangeDetectionStrategy} from "@angular/core"
import {CM_LOGGER_APP} from "@app/common/services/logging"
import {MatDialog} from "@angular/material/dialog"
import {MatProgressBarModule} from "@angular/material/progress-bar"
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner"
import {MatSnackBar} from "@angular/material/snack-bar"
import {MatTooltipModule} from "@angular/material/tooltip"
import {BackgroundOperationService} from "@app/platform/services/background-operation/background-operation.service"
import {DialogComponent} from "@common/components/dialogs/dialog/dialog.component"
import {BackgroundOperationItem, BackgroundOperationState} from "@app/common/models/background-operation/background-operation-item"
import {Subscription} from "rxjs"
import {FilesService} from "@app/common/services/files/files.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-background-operation-list",
    templateUrl: "background-operation-list.component.html",
    styleUrls: ["background-operation-list.component.scss"],
    imports: [MatProgressBarModule, MatTooltipModule, MatProgressSpinnerModule],
    host: {
        popover: "manual",
    },
})
export class BackgroundOperationListComponent implements OnInit, OnDestroy {
    private readonly logger = inject(CM_LOGGER_APP)
    private readonly elementRef = inject(ElementRef)
    subscriptions: Array<Subscription> = []
    readonly $operations = signal<Array<BackgroundOperationItem>>([])
    readonly $minimized = signal(false)
    private readonly backgroundOperationService = inject(BackgroundOperationService)
    readonly mdDialog = inject(MatDialog)
    private readonly snackBar = inject(MatSnackBar)

    constructor() {
        // Show as popover so the element is rendered in the browser's top layer,
        // at the same level as CDK overlay dialogs (which also use the Popover API since CDK v21).
        afterNextRender(() => {
            this.elementRef.nativeElement.showPopover()
        })
    }

    ngOnInit() {
        const backgroundOperationItemSubscription: Subscription = this.backgroundOperationService.backgroundOperationItem$.subscribe(
            (backgroundOperationItem) => {
                this.$operations.update((current) => [...current, backgroundOperationItem])
                this.bringToFront()
                backgroundOperationItem.progressObservable?.subscribe({
                    next: (progress) => {
                        backgroundOperationItem.state = BackgroundOperationState.InProgress
                        backgroundOperationItem.progress = progress
                        this.$operations.update((ops) => [...ops])
                    },
                    error: () => {
                        backgroundOperationItem.state = BackgroundOperationState.Error
                        this.$operations.update((ops) => [...ops])
                    },
                    complete: () => {
                        backgroundOperationItem.state = BackgroundOperationState.Completed
                        if (backgroundOperationItem.discardOnCompletion) {
                            this.$operations.set(this.$operations().filter((operation) => operation !== backgroundOperationItem))
                        } else {
                            this.$operations.update((ops) => [...ops])
                        }
                    },
                })
            },
        )
        this.subscriptions.push(backgroundOperationItemSubscription)

        const closeToolbarAndCleanSubscription: Subscription = this.backgroundOperationService.closeToolbarAndCleanOperations$.subscribe(
            () => {
                this.closeAndCancel()
            },
            (error) => {
                this.logger.debug("Error closing toolbar:", error)
            },
        )
        this.subscriptions.push(closeToolbarAndCleanSubscription)
    }

    minimize(): void {
        this.$minimized.set(true)
    }

    maximize(): void {
        this.$minimized.set(false)
    }

    closeAndCancel(): void {
        for (const operation of this.$operations()) {
            if (operation.state === BackgroundOperationState.InProgress) {
                this.confirmCancellation()
                return
            }
        }
        this.$operations.set([])
        this.maximize()
    }

    confirmCancellation() {
        const dialogRef = this.mdDialog.open(DialogComponent, {
            disableClose: false,
            width: "400px",
            data: {
                title: "Cancel operations",
                message: "The operations are not completed yet. Do you want to cancel all operations?",
                confirmLabel: "Cancel operations",
                cancelLabel: "Continue operations",
            },
        })

        dialogRef.afterClosed().subscribe((confirmed) => {
            if (confirmed) {
                this.cancelAllOperations()
            }
        })
    }

    cancelAllOperations(): void {
        const cleanedOperations: BackgroundOperationItem[] = []
        for (const operation of this.$operations()) {
            if (operation.cancellable && (operation.state === BackgroundOperationState.Waiting || operation.state === BackgroundOperationState.InProgress))
                operation.cancel()
            else cleanedOperations.push(operation)
        }
        this.$operations.set(cleanedOperations)
        if (this.$operations().length > 0) this.snackBar.open("The remaining operations are not cancellable.", "", {duration: 3000})
    }

    private bringToFront() {
        const el = this.elementRef.nativeElement
        el.hidePopover()
        el.showPopover()
    }

    ngOnDestroy() {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe()
        }
    }

    downloadFile(event: Event, filename: string, url: string) {
        event.preventDefault()
        FilesService.downloadFile(filename, url)
    }
}
